import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { socketClient } from "../api/socket";
import { logout as apiLogout, verifyGoogleIdToken } from "../api/auth";
import type { UserData } from "../types";
import { GUEST } from "../types";

interface AuthContextValue {
  user: UserData;
  isAuthenticated: boolean;
  signInWithGoogle: (idToken: string) => Promise<UserData>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserData>(socketClient.userdata);

  // The websocket handshake is the canonical source of truth for the
  // authenticated user (see backend/handlers/websocket_handler.py). When
  // the socket reconnects with a new auth cookie the `connected` frame
  // contains the fresh user data, so we wire ourselves to that.
  useEffect(() => {
    const off = socketClient.on<UserData>("connected", (msg) => {
      setUser({ ...GUEST, ...(msg.content || {}), initialized: true });
    });
    socketClient.connect().then((data) => setUser(data));
    return off;
  }, []);

  const signInWithGoogle = useCallback(async (idToken: string) => {
    const data = await verifyGoogleIdToken(idToken);
    // The verify endpoint sets fresh auth cookies; reconnect the WS so the
    // handshake picks up the new user identity.
    const refreshed = await socketClient.reconnect();
    setUser(refreshed);
    return { ...refreshed, tos: data.tos } as UserData;
  }, []);

  const signOut = useCallback(async () => {
    try {
      await apiLogout();
    } catch (err) {
      console.error("Logout failed", err);
    }
    socketClient.send("logout", "");
    setUser({ ...GUEST });
    await socketClient.reconnect();
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      // Guest WS clients use username "guest" and id -1. The backend used to send
      // only `username` on `connected`, leaving id at -1 from GUEST merge — treat
      // any non-guest username as signed-in. Prefer id !== -1 when present.
      isAuthenticated:
        !!user &&
        user.initialized &&
        ((typeof user.id === "number" && user.id !== -1) ||
          (!!user.username && user.username !== "guest")),
      signInWithGoogle,
      signOut,
    }),
    [user, signInWithGoogle, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
